<template>
    <div class="modal fade" id="manual_transaction_modal" ref="modalEl" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-560px">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="fw-bolder">{{ $t('pages.transaction.newTitle') }}</h2>
                    <div data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1"><inline-svg src="/media/icons/duotune/arrows/arr061.svg"/></span>
                    </div>
                </div>

                <el-form @submit.prevent="onSubmit" :model="form" ref="formRef">
                    <div class="modal-body py-8 px-lg-10">
                        <!-- Direction segment -->
                        <div class="mtx-field">
                            <label class="mtx-label">{{ $t('common.transaction.direction.1') }} / {{ $t('common.transaction.direction.2') }}</label>
                            <div class="mtx-segment">
                                <button type="button"
                                    class="mtx-segment-btn"
                                    :class="{'mtx-segment-btn--active mtx-segment-btn--income': form.direction_id === 1}"
                                    @click="form.direction_id = 1">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
                                        <polyline points="6 9 12 15 18 9"/>
                                    </svg>
                                    {{ $t('common.transaction.direction.1') }}
                                </button>
                                <button type="button"
                                    class="mtx-segment-btn"
                                    :class="{'mtx-segment-btn--active mtx-segment-btn--expense': form.direction_id === 2}"
                                    @click="form.direction_id = 2">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
                                        <polyline points="18 15 12 9 6 15"/>
                                    </svg>
                                    {{ $t('common.transaction.direction.2') }}
                                </button>
                            </div>
                        </div>

                        <!-- Total -->
                        <div class="mtx-field">
                            <label class="mtx-label">{{ $t('common.transaction.summary.total') }} <span class="mtx-required">*</span></label>
                            <el-input v-model="form.total_amount"
                                oninput="value=value.replace(/[^0-9.]/g,'')"
                                :placeholder="$t('common.transaction.payment.amountPlaceholder')"/>
                        </div>

                        <!-- Note -->
                        <div class="mtx-field">
                            <label class="mtx-label">{{ $t('common.transaction.payment.note') }}</label>
                            <el-input v-model="form.note" type="textarea" :rows="2"
                                :placeholder="$t('common.transaction.payment.notePlaceholder')"/>
                        </div>

                        <!-- Initial payment (optional) -->
                        <div class="mtx-section">
                            <div class="mtx-section-head">
                                <label class="mtx-section-title">{{ $t('pages.module.VEHICLE_WORK_ORDER.save.payment.initialTitle') }}</label>
                                <span class="mtx-section-pill">{{ $t('common.optional') }}</span>
                            </div>
                            <div class="mtx-grid">
                                <div class="mtx-field">
                                    <label class="mtx-sub-label">{{ $t('common.transaction.payment.amount') }}</label>
                                    <el-input v-model="form.paid_amount"
                                        oninput="value=value.replace(/[^0-9.]/g,'')"
                                        :placeholder="$t('common.transaction.payment.amountPlaceholder')"/>
                                </div>
                                <div class="mtx-field">
                                    <label class="mtx-sub-label">{{ $t('common.transaction.payment.method') }}</label>
                                    <el-select v-model="form.payment_method_id"
                                        class="w-100"
                                        :placeholder="$t('common.transaction.payment.methodPlaceholder')"
                                        :disabled="!hasPaidAmount">
                                        <el-option v-for="m in paymentMethods" :key="m.id"
                                            :label="methodLabel(m)" :value="m.id"/>
                                    </el-select>
                                </div>
                                <div v-if="hasPaidAmount" class="mtx-field mtx-field--full">
                                    <label class="mtx-sub-label">{{ $t('common.transaction.payment.cashRegister') }}</label>
                                    <el-select v-model="form.cash_register_id"
                                        class="w-100"
                                        :placeholder="$t('common.transaction.payment.cashRegisterPlaceholder')">
                                        <el-option v-for="c in cashRegisters" :key="c.id"
                                            :label="c.name + (c.is_default ? ' ' + $t('common.transaction.payment.cashRegisterDefault') : '')"
                                            :value="c.id"/>
                                    </el-select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal" :disabled="loading">
                            {{ $t('common.cancel') }}
                        </button>
                        <button type="submit" class="btn btn-primary"
                            :disabled="!canSubmit || loading"
                            :data-kt-indicator="loading ? 'on' : null">
                            <span v-if="!loading" class="indicator-label">{{ $t('common.save') }}</span>
                            <span v-if="loading" class="indicator-progress">
                                {{ $t('messages.wait') }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
import transactionService from "@/components/payment/transactionService";

function emptyForm() {
    return {
        direction_id: 1,
        total_amount: '',
        note: '',
        paid_amount: '',
        payment_method_id: null,
        cash_register_id: null,
    };
}

export default {
    name: "ManualTransactionDialog",
    emits: ['created'],
    data() {
        return {
            form: emptyForm(),
            loading: false,
        };
    },
    computed: {
        paymentMethods() {
            return transactionService.state.paymentMethods;
        },
        cashRegisters() {
            return transactionService.state.cashRegisters;
        },
        hasPaidAmount() {
            return parseFloat(this.form.paid_amount) > 0;
        },
        canSubmit() {
            const total = parseFloat(this.form.total_amount);
            if (!(total > 0)) return false;
            if (this.hasPaidAmount && !this.form.payment_method_id) return false;
            return true;
        },
    },
    methods: {
        open() {
            this.form = emptyForm();
            const def = this.cashRegisters.find(c => c.is_default);
            if (def) this.form.cash_register_id = def.id;
            transactionService.ensurePaymentMethods();
            transactionService.ensureCashRegisters().then(() => {
                if (!this.form.cash_register_id) {
                    const d = this.cashRegisters.find(c => c.is_default);
                    if (d) this.form.cash_register_id = d.id;
                }
            });
            this.showModal(this.$refs.modalEl);
        },
        methodLabel(method) {
            if (!method) return '-';
            const key = 'common.transaction.payment.methodCode.' + method.code;
            const t = this.$t(key);
            return t === key ? method.name : t;
        },
        async onSubmit() {
            if (!this.canSubmit) return;
            this.loading = true;
            const body = {
                direction_id: this.form.direction_id,
                total_amount: parseFloat(this.form.total_amount),
            };
            if (this.form.note) body.note = this.form.note;
            const paid = parseFloat(this.form.paid_amount);
            if (paid > 0) {
                body.paid_amount = paid;
                body.payment_method_id = this.form.payment_method_id;
                if (this.form.cash_register_id) body.cash_register_id = this.form.cash_register_id;
            }
            try {
                const res = await this.$store.dispatch('transaction/add', { formData: body });
                if (res?.status) {
                    this.$message.success(this.$t('messages.saveOk'));
                    this.hideModal(this.$refs.modalEl);
                    this.$emit('created', res.data);
                }
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.mtx-desc {
    color: #8e8e93;
    font-size: 12.5px;
    margin-bottom: 14px;
}

.mtx-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 14px;
}

.mtx-field--full {
    grid-column: 1 / -1;
}

.mtx-label {
    font-size: 12px;
    font-weight: 600;
    color: #1c1c1e;
}

.mtx-sub-label {
    font-size: 11px;
    font-weight: 600;
    color: #8e8e93;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.mtx-required { color: #ef4444; }

.mtx-segment {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
    padding: 3px;
    background: rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 9px;
}

.mtx-segment-btn {
    border: none;
    background: transparent;
    color: #8e8e93;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    padding: 8px 10px;
    border-radius: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: background 0.18s, color 0.18s, box-shadow 0.18s;
}

.mtx-segment-btn:hover {
    color: #1c1c1e;
}

.mtx-segment-btn--active {
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.mtx-segment-btn--income.mtx-segment-btn--active {
    color: #28a745;
}

.mtx-segment-btn--expense.mtx-segment-btn--active {
    color: #ef4444;
}

.mtx-section {
    margin-top: 18px;
    padding: 14px 16px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: rgba(0, 0, 0, 0.02);
    border-radius: 10px;
}

.mtx-section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
}

.mtx-section-title {
    font-weight: 700;
    font-size: 13px;
    color: #1c1c1e;
}

.mtx-section-pill {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(var(--panel-accent-rgb, 0, 122, 255), 0.12);
    color: var(--panel-accent-text, #007AFF);
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.mtx-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 12px;
}

@media (max-width: 580px) {
    .mtx-grid {
        grid-template-columns: 1fr;
    }
}

/* Dark / midnight theme overrides */
html.dark .mtx-label,
html.midnight .mtx-label,
html.dark .mtx-section-title,
html.midnight .mtx-section-title {
    color: #EAE6E1;
}

html.dark .mtx-sub-label,
html.midnight .mtx-sub-label,
html.dark .mtx-desc,
html.midnight .mtx-desc {
    color: #9A9CAE;
}

html.dark .mtx-segment-btn,
html.midnight .mtx-segment-btn { color: #9A9CAE; }

html.dark .mtx-segment-btn:hover,
html.midnight .mtx-segment-btn:hover { color: #EAE6E1; }

html.dark .mtx-section,
html.midnight .mtx-section {
    background: rgba(255, 255, 255, 0.025);
    border-color: rgba(255, 255, 255, 0.08);
}

html.dark .mtx-segment,
html.midnight .mtx-segment {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.08);
}

html.dark .mtx-segment-btn--active,
html.midnight .mtx-segment-btn--active {
    background: rgba(255, 255, 255, 0.12);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

html.dark .mtx-segment-btn--income.mtx-segment-btn--active,
html.midnight .mtx-segment-btn--income.mtx-segment-btn--active { color: #34D399; }

html.dark .mtx-segment-btn--expense.mtx-segment-btn--active,
html.midnight .mtx-segment-btn--expense.mtx-segment-btn--active { color: #F87171; }

/* Cream theme */
html.cream .mtx-label,
html.cream .mtx-section-title { color: #3D3832; }

html.cream .mtx-sub-label,
html.cream .mtx-desc { color: #847C72; }

html.cream .mtx-section {
    background: rgba(61, 56, 50, 0.04);
    border-color: rgba(61, 56, 50, 0.1);
}

html.cream .mtx-segment {
    background: rgba(61, 56, 50, 0.05);
    border-color: rgba(61, 56, 50, 0.1);
}

html.cream .mtx-segment-btn { color: #847C72; }
html.cream .mtx-segment-btn:hover { color: #3D3832; }
html.cream .mtx-segment-btn--active { background: #FEFCF8; }
html.cream .mtx-segment-btn--income.mtx-segment-btn--active { color: #059669; }

/* Modal cancel button — Metronic btn-light tum temalarda override */
.btn.btn-light,
.btn.btn-light:hover,
.btn.btn-light:focus,
.btn.btn-light:active,
.btn.btn-light:focus-visible {
    border-color: rgba(0, 0, 0, 0.08) !important;
    color: #1c1c1e !important;
    box-shadow: none !important;
}

.btn.btn-light,
.btn.btn-light:focus { background-color: rgba(0, 0, 0, 0.05) !important; }
.btn.btn-light:hover,
.btn.btn-light:active { background-color: rgba(0, 0, 0, 0.09) !important; }

html.dark .btn.btn-light,
html.dark .btn.btn-light:hover,
html.dark .btn.btn-light:focus,
html.dark .btn.btn-light:active,
html.midnight .btn.btn-light,
html.midnight .btn.btn-light:hover,
html.midnight .btn.btn-light:focus,
html.midnight .btn.btn-light:active {
    border-color: rgba(255, 255, 255, 0.1) !important;
    color: #EAE6E1 !important;
}

html.dark .btn.btn-light,
html.dark .btn.btn-light:focus,
html.midnight .btn.btn-light,
html.midnight .btn.btn-light:focus { background-color: rgba(255, 255, 255, 0.07) !important; }
html.dark .btn.btn-light:hover,
html.dark .btn.btn-light:active,
html.midnight .btn.btn-light:hover,
html.midnight .btn.btn-light:active { background-color: rgba(255, 255, 255, 0.13) !important; }

html.cream .btn.btn-light,
html.cream .btn.btn-light:hover,
html.cream .btn.btn-light:focus,
html.cream .btn.btn-light:active {
    border-color: rgba(61, 56, 50, 0.1) !important;
    color: #3D3832 !important;
}

html.cream .btn.btn-light,
html.cream .btn.btn-light:focus { background-color: rgba(61, 56, 50, 0.06) !important; }
html.cream .btn.btn-light:hover,
html.cream .btn.btn-light:active { background-color: rgba(61, 56, 50, 0.1) !important; }

</style>
